import { Injectable } from "@angular/core";
import { StatusPedido } from "../models/pedido";
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import { PedidoService } from "./pedido.service";
import { KanbanGrupo, MovimentacaoManual } from "../kanbam/KanbamGrupo";
import { KanbanPedido } from "../kanbam/KanbamPedido";
import { EventService } from "./event.service";
import { Subject, interval } from "rxjs";
import { KanbamFiltro, KanbanFiltroKey } from "../kanbam/KanbamFiltro";
import { ModoConfirmacao } from "../models/ModoConfirmacao.model";
import { AppService } from "./app.service";
import { NotificacaoService } from "./notificacao.service";

@Injectable({
  providedIn: "root",
})
export class KanbanService {
  readonly TEMPO_ATUALIZAZAO_PEDIDOS = 60000;

  private _gruposOrdenados: KanbanGrupo[] = [];
  private _idPedidoMaisRecente: number = 1;

  onChangeFiltroPedidos$ = new Subject<KanbamFiltro>();
  eventoAposAdicionarPedidoNoKanban$ = new Subject<KanbanPedido>();
  eventoAposRemoverPedidoNoKanban$ = new Subject<KanbanPedido>();

  lixeira: KanbanPedido[] = [];
  grupos: KanbanGrupo[] = [];
  filtros: KanbamFiltro[] = [];

  _modoConfirmacaoPedido = ModoConfirmacao.MANUAL;

  constructor(
    private pedidoService: PedidoService,
    private eventService: EventService,
    private notificacaoService: NotificacaoService
  ) {
    this.grupos.push(new KanbanGrupo("Agendado", 0, StatusPedido.AGENDADO));
    this.grupos.push(new KanbanGrupo("Pendente", 1, StatusPedido.PENDENTE));
    this.grupos.push(new KanbanGrupo("Produção", 2, StatusPedido.EM_PRODUCAO));
    this.grupos.push(
      new KanbanGrupo("Entregando", 3, StatusPedido.ENTREGRANDO)
    );
    this.grupos.push(new KanbanGrupo("Finalizado", 4, StatusPedido.FINALIZADO));
    this.grupos.push(new KanbanGrupo("Cancelado", 5, StatusPedido.CANCELADO));

    this.filtros.push(new KanbamFiltro("TODOS", "Todos", "#353c50"));
    this.filtros.push(new KanbamFiltro("DELIVERY", "Delivery", "#e36481"));
    this.filtros.push(new KanbamFiltro("RETIRADA", "Retirada", "#df9e41"));
    this.filtros.push(new KanbamFiltro("CONSUMO", "Consumo Local", "#a758f5"));

    this._gruposOrdenados = this.grupos.sort((a, b) => a.ordem - b.ordem);

    const modoConfirmacaoSalvo = (localStorage.getItem(
      "monitor@modo-confirmacao"
    ) ?? "M") as ModoConfirmacao;
    this.modoConfirmacaoPedido = modoConfirmacaoSalvo;

    this.eventService.onGetListaPedidos.subscribe((pedidos) => {
      pedidos.forEach((pedido) => {
        const kanbanPedido = new KanbanPedido(pedido);
        this.adicionarItem(kanbanPedido);
      });
    });

    this.buscarNovosPedidosNoServidor();
    interval(this.TEMPO_ATUALIZAZAO_PEDIDOS).subscribe(() => {
      this.buscarNovosPedidosNoServidor();
    });

    interval(5000).subscribe(() => {
      this.atualizarTempoDoPedidoKanban();
      // this.removerPedidosFinalizados();
    });

    this.onChangeFiltroPedidos$.subscribe((filtro: KanbamFiltro) => {
      if (filtro.key !== "TODOS") {
        const todosFiltrosEstaoSelecionados = this.filtros
          .filter((filtro) => filtro.key !== "TODOS")
          .every((filtro) => filtro.ativo);
        this.buscarFiltroPelaKey("TODOS").ativo = todosFiltrosEstaoSelecionados;
        return;
      }

      if (filtro.ativo) {
        this.selecionarTodosOsFiltros();
      } else {
        this.deselecionarTodosOsFiltros();
      }
    });

    this.eventoAposAdicionarPedidoNoKanban$.subscribe(() => {
      this.atualizarContagemPedidosParaFiltros();
    });

    this.eventoAposRemoverPedidoNoKanban$.subscribe(() => {
      this.atualizarContagemPedidosParaFiltros();
    });

    this.notificacaoService.eventoNoticacaoPedidoPedente$.subscribe(() => {
      this.notificarPedidosPendente();
    });
  }

  set modoConfirmacaoPedido(valor: ModoConfirmacao) {
    this._modoConfirmacaoPedido = valor;
    localStorage.setItem("monitor@modo-confirmacao", valor);
  }

  get modoConfirmacaoPedido(): ModoConfirmacao {
    return this._modoConfirmacaoPedido;
  }

  get gruposOrdenados(): KanbanGrupo[] {
    return this._gruposOrdenados;
  }

  get idPedidoMaisRecente() {
    return this._idPedidoMaisRecente;
  }

  get modoConfirmacaoAutomaticoEstaAtivo(): boolean {
    return this.modoConfirmacaoPedido === ModoConfirmacao.AUTOMATICO;
  }

  get listaDeIdDosPedidosNoKanban(): string[] {
    const lista = [];
    this.iterarTodosOsPedidosDoKanban((_, pedido) => {
      lista.push(pedido.pedido.id);
    });
    return lista;
  }

  buscarNovosPedidosNoServidor() {
    const tempoLimite =
      this.notificacaoService.tempoPermanenciaNoMonitorEmHoras;
    this.pedidoService
      .getNovosPedidos(
        this.idPedidoMaisRecente,
        this.listaStringAtualizacaoDosPedidos,
        tempoLimite
      )
      .then((resposta) => {
        for (const pedido of resposta.Pedidos) {
          const kanbamPedido = new KanbanPedido(pedido);
          kanbamPedido.ehNovoPedido = true;
          this.adicionarItem(kanbamPedido);
        }

        if (!!resposta?.Atualizacao) {
          for (const atualizacao of resposta?.Atualizacao) {
            const pedidoKanban = this.buscarPedidoNoKanban(atualizacao.id);

            if (!pedidoKanban) continue;

            let [grupo, pedido] = pedidoKanban;
            const statusAtual = Number(pedido.pedido.status);
            const novoStatus = Number(atualizacao.status);
            pedido.setarAtualizacao(Number(atualizacao.Atualizacao));
            if (statusAtual !== novoStatus)
              this.movimentarItemAutomaticamenteSemAtualizarStatusNoServidor(
                pedido,
                statusAtual,
                novoStatus
              );
          }
        }
      });
  }

  removerPedidosFinalizados() {
    const tempoPermenenciaNoMonitor =
      this.notificacaoService.tempoPermanenciaNoMonitorEmMilisegundos;

    const timestampAgora = new Date().getTime();

    this.iterarTodosOsPedidosDoKanban((grupo, pedido) => {
      if (![4, 5].includes(Number(pedido.pedido.status))) return;

      const timestampDoPedido = new Date(pedido.pedido.DataHora).getTime();

      const pedidoDeveSerRemovido =
        timestampDoPedido + tempoPermenenciaNoMonitor < timestampAgora;

      if (pedidoDeveSerRemovido) {
        this.removerPedidoDoKanban(pedido.idDoPedido);
      }
    });
  }

  atualizarTempoDoPedidoKanban() {
    this.iterarTodosOsPedidosDoKanban((_, pedido) => {
      const horaPedido = new Date(pedido.pedido.DataHora);
      const horaEntrega = this.pedidoService.addicionarMinutos(
        horaPedido,
        Number(pedido.pedido.TempoEntrega)
      );
      pedido.pedido.TempoRestante =
        this.pedidoService.calcularTempoRestante(horaEntrega);
      pedido.pedido.Progresso = this.pedidoService.calcularProgressoPedido(
        pedido.pedido
      );
    });
  }

  getQuantidadePedidosPorTipoFiltro(key: KanbanFiltroKey): number {
    return this.buscarFiltroPelaKey(key)?.quantidade ?? 0;
  }

  atualizarContagemPedidosParaFiltros() {
    const filtroTodos = this.buscarFiltroPelaKey("TODOS");
    const filtroDelivery = this.buscarFiltroPelaKey("DELIVERY");
    const filtroRetirada = this.buscarFiltroPelaKey("RETIRADA");
    const filtroConsumo = this.buscarFiltroPelaKey("CONSUMO");

    filtroConsumo.quantidade =
      filtroDelivery.quantidade =
      filtroRetirada.quantidade =
      filtroTodos.quantidade =
        0;

    this.iterarTodosOsPedidosDoKanban((_, pedido) => {
      filtroTodos.quantidade += 1;

      if (pedido.pedido.Pedido.OpcaoEntrega === "E") {
        filtroDelivery.quantidade += 1;
      } else if (pedido.pedido.Pedido.OpcaoEntrega === "R") {
        filtroRetirada.quantidade += 1;
      } else if (["L", "C"].includes(pedido.pedido.Pedido.OpcaoEntrega)) {
        filtroConsumo.quantidade += 1;
      }
    });
  }

  notificarPedidosPendente(): void {
    if (!!this.getGrupoPelostatus(StatusPedido.PENDENTE).quantidadeDeItens) {
      this.notificacaoService.notificar();
    }
  }

  buscarFiltroPelaKey(key: KanbanFiltroKey): KanbamFiltro {
    return this.filtros.find((filtro) => filtro.key === key);
  }

  selecionarTodosOsFiltros(): void {
    this.filtros.forEach((filtro) => {
      filtro.ativo = true;
    });
  }

  deselecionarTodosOsFiltros(): void {
    this.filtros.forEach((filtro) => {
      filtro.ativo = false;
    });
  }

  buscarPedidoNoKanban(id: string): [KanbanGrupo, KanbanPedido] | null {
    this.grupos.forEach;
    for (const grupo of this.grupos) {
      const pedido = grupo.itens.find(
        (item) => Number(item.pedido.id) === Number(id)
      );
      if (!!pedido) {
        return [grupo, pedido];
      }
    }
    return null;
  }

  buscarPedidoNoGrupo(id: string, grupo: KanbanGrupo): KanbanPedido | null {
    const pedido = grupo.itens.find(
      (item) => Number(item.pedido.id) === Number(id)
    );
    return pedido ?? null;
  }

  atualizarStatusENumeroDeAtualizacaoDoPedido(
    idPedido: number | string,
    status: string | number,
    atualizacao: string | number
  ) {
    const pedido = this.buscarPedidoNoKanban(String(idPedido));

    if (!pedido) return;

    const [kanbamGrupo, kanbanPedido] = pedido;
    if (
      kanbanPedido.numeroDeAtualizacaoDoPedidoNoKanbanEhMaisRecente(atualizacao)
    )
      return;

    kanbanPedido.setarAtualizacao(atualizacao);
    kanbanPedido.setarStatus(status);
  }

  get temAlgumFiltroAtivo(): boolean {
    return !this.buscarFiltroPelaKey("TODOS").ativo;
  }

  pedidoPassouNoFiltro(pedido: KanbanPedido) {
    const opcaoEntrega = pedido.pedido.Pedido.OpcaoEntrega;
    const ehPedidoAgendamento = !!pedido.pedido.Pedido?.DadosAgendamento;

    if (this.buscarFiltroPelaKey("TODOS").ativo) {
      return true;
    }

    if (opcaoEntrega === "E") {
      return this.buscarFiltroPelaKey("DELIVERY").ativo;
    } else if (opcaoEntrega === "R") {
      return this.buscarFiltroPelaKey("RETIRADA").ativo;
    } else if (opcaoEntrega === "L" || opcaoEntrega === "C") {
      return this.buscarFiltroPelaKey("CONSUMO").ativo;
    } else return true;
  }

  getGrupoPelostatus(
    status: StatusPedido | number | string
  ): KanbanGrupo | null {
    const grupo = this.grupos.filter(
      (grupo) => Number(grupo.status) === Number(status)
    );
    return grupo?.length ? grupo[0] : null;
  }

  verificarSeProdutoJaFoiAdicionadoNoCambam(
    id: number | string
  ): [KanbanGrupo, KanbanPedido] | null {
    return this.buscarPedidoNoKanban(String(id));
  }

  adicionarItem(item: KanbanPedido) {
    const statusPedido = Number(item.pedido.status);

    const grupo = this.getGrupoPelostatus(statusPedido);

    if (!grupo) return;

    const produtoEncontradoNoKanban =
      this.verificarSeProdutoJaFoiAdicionadoNoCambam(item.pedido.id);

    if (!produtoEncontradoNoKanban) {
      item.inicicarEfeitoTransicao;
      grupo.adicionarItem(item);
      item.finalizarEfeitoTransicao();

      this.notificacaoService.notificar();

      if (
        item.ehNovoPedido &&
        item.pedidoTemStatusPendente &&
        this.modoConfirmacaoAutomaticoEstaAtivo
      ) {
        this.autoConfirmarPedido(item);
      }
      this._idPedidoMaisRecente = Math.max(
        this._idPedidoMaisRecente,
        Number(item.pedido.id)
      );
      this.eventoAposAdicionarPedidoNoKanban$.next(item);
    } else {
      const [grupoDoPedido, pedidoEncontrado] = produtoEncontradoNoKanban;
      this.atualizarItemNoKanban(item, grupoDoPedido, pedidoEncontrado);
    }

    this.atualizarTempoDoPedidoKanban();
  }

  autoConfirmarPedido(pedido: KanbanPedido) {
    this.movimentarItemAutomaticamente(pedido, 1, 2);
  }

  atualizarItemNoKanban(
    pedidoRecebido: KanbanPedido,
    grupoAtual: KanbanGrupo,
    pedidoNoKanban: KanbanPedido
  ) {
    if (
      pedidoNoKanban.numeroDeAtualizacaoDoPedidoNoKanbanEhMaisRecente(
        pedidoRecebido.pedido.Atualizacao
      )
    )
      return;

    pedidoNoKanban.pedido = { ...pedidoRecebido.pedido };

    const pedidoTeveAlteracaoDeStatus =
      Number(pedidoRecebido.pedido.status) !== grupoAtual.numeroDoStatus;
    if (!pedidoTeveAlteracaoDeStatus) {
      return;
    }

    this.movimentarItemAutomaticamente(
      pedidoNoKanban,
      grupoAtual.numeroDoStatus,
      Number(pedidoNoKanban.pedido.status)
    );
  }

  async movimentarItemManualmente(
    evento: CdkDragDrop<KanbanGrupo>
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const movimentacao = new MovimentacaoManual(evento);

      try {
        this.validarMovimentacaoManual(movimentacao);
      } catch (erro) {
        reject(erro);
      }

      if (movimentacao.movimentouParaOutroGrupo) {
        const pedido = movimentacao.item.pedido;
        this.eventService.aposMoverItemKanban$.next(movimentacao.item);
        movimentacao.item.setarStatus(movimentacao.grupoAtual.status);
        movimentacao.item.registrarStatusInicioAtualizacaoNoServidor();
        transferArrayItem(
          movimentacao.grupoAnterior.itens,
          movimentacao.grupoAtual.itens,
          movimentacao.posicaoAnterior,
          movimentacao.posicaoAtual
        );
        this.pedidoService
          .salvarStatusPedido(
            pedido.Hash,
            String(movimentacao.grupoAtual.status)
          )
          .then((resposta) => {
            if (!!resposta?.StatusPedidos?.length) {
              for (const pedido of resposta.StatusPedidos) {
                this.atualizarStatusENumeroDeAtualizacaoDoPedido(
                  pedido.id,
                  pedido.Status,
                  pedido.Atualizacao
                );
              }
            }

            movimentacao.item.registrarStatusTerminoAtualizacaoNoServidor();
          })
          .catch((erro) => {
            movimentacao.item.registrarStatusTerminoAtualizacaoNoServidor();
            console.log(erro);
          });
      } else {
        moveItemInArray(
          movimentacao.grupoAnterior.itens,
          movimentacao.posicaoAnterior,
          movimentacao.posicaoAtual
        );
      }
    });
  }

  voltarPedidoParaStatusAgendado(pedido: KanbanPedido, grupo: KanbanGrupo) {
    this.movimentarItemAutomaticamente(
      pedido,
      pedido.statusDoPedido,
      Number(StatusPedido.AGENDADO)
    );
  }

  async movimentarItemAutomaticamente(
    pedidoKanban: KanbanPedido,
    statusAnterior: number,
    statusAtual: number
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.eventService.aposMoverItemKanban$.next(pedidoKanban);

      pedidoKanban.setarStatus(statusAtual);
      pedidoKanban.registrarStatusInicioAtualizacaoNoServidor();
      pedidoKanban.inicicarEfeitoTransicao();

      setTimeout(() => {
        const pedido = pedidoKanban.pedido;
        const grupoAnterior = this.getGrupoPelostatus(statusAnterior);
        const grupoAtual = this.getGrupoPelostatus(statusAtual);

        const posicaoAnterior = grupoAnterior.getPosicaoDoPedidoNoGrupo(
          pedido.id
        );
        const posicaoAtual = grupoAtual.quantidadeDeItens;

        transferArrayItem(
          grupoAnterior.itens,
          grupoAtual.itens,
          posicaoAnterior,
          posicaoAtual
        );
        const statusMovimentacao = this.pedidoService
          .salvarStatusPedido(pedido.Hash, String(grupoAtual.status))
          .then((resposta) => {
            if (!!resposta?.StatusPedidos?.length) {
              for (const pedido of resposta.StatusPedidos) {
                this.atualizarStatusENumeroDeAtualizacaoDoPedido(
                  pedido.id,
                  pedido.Status,
                  pedido.Atualizacao
                );
              }
            }
            pedidoKanban.registrarStatusTerminoAtualizacaoNoServidor();
          })
          .catch((erro) => {
            console.log(erro);
          });
      }, 200);

      setTimeout(() => {
        pedidoKanban.finalizarEfeitoTransicao();
      }, 250);
    });
  }

  movimentarItemAutomaticamenteSemAtualizarStatusNoServidor(
    pedidoKanban: KanbanPedido,
    statusAnterior: number,
    statusAtual: number
  ): void {
    pedidoKanban.inicicarEfeitoTransicao();

    setTimeout(() => {
      const pedido = pedidoKanban.pedido;
      const grupoAnterior = this.getGrupoPelostatus(statusAnterior);
      const grupoAtual = this.getGrupoPelostatus(statusAtual);

      const posicaoAnterior = grupoAnterior.getPosicaoDoPedidoNoGrupo(
        pedido.id
      );
      const posicaoAtual = grupoAtual.quantidadeDeItens;

      transferArrayItem(
        grupoAnterior.itens,
        grupoAtual.itens,
        posicaoAnterior,
        posicaoAtual
      );
      pedidoKanban.setarStatus(statusAtual);
    }, 200);

    setTimeout(() => {
      pedidoKanban.finalizarEfeitoTransicao();
      this.eventService.aposMoverItemKanban$.next(pedidoKanban);
    }, 250);
  }

  validarMovimentacaoManual(movimentacao: MovimentacaoManual): void {}

  avancarTodosDoGrupo(grupo: KanbanGrupo) {
    for (const pedido of grupo.itens) {
      this.movimentarItemAutomaticamente(
        pedido,
        grupo.status,
        grupo.numeroDoProximoStatus
      );
    }
  }

  voltarTodosDoGrupo(grupo: KanbanGrupo) {
    for (const pedido of grupo.itens) {
      this.movimentarItemAutomaticamente(
        pedido,
        grupo.status,
        grupo.numeroDoStatusAnterior
      );
    }
  }

  removerPedidoDoKanban(idPedido: number | string) {
    const dadosPedido = this.buscarPedidoNoKanban(String(idPedido));

    if (!dadosPedido) return;

    let [grupo, pedido] = dadosPedido;
    pedido.inicicarEfeitoTransicao();
    setTimeout(() => {
      grupo.removerPedido(pedido.idDoPedido);
      this.eventoAposRemoverPedidoNoKanban$.next(pedido);
    }, 200);
  }

  iterarTodosOsPedidosDoKanban(
    callback: (grupo: KanbanGrupo, pedido: KanbanPedido) => void
  ) {
    for (const grupo of this.grupos) {
      for (const item of grupo.itens) {
        callback(grupo, item);
      }
    }
  }

  get listaStringAtualizacaoDosPedidos() {
    const itens = [];
    this.iterarTodosOsPedidosDoKanban((grupo, pedido) => {
      itens.push(`${pedido.pedido.id}:${pedido.pedido.Atualizacao}`);
    });
    return itens.length ? itens.join(",") : "";
  }

  limparGrupo(grupo: KanbanGrupo) {
    grupo.itens.forEach((kanbanPedido) => {
      kanbanPedido.registrarStatusInicioAtualizacaoNoServidor();
    });

    const arrayHashs = grupo.getlistaDeHashDosPedidosNoGrupo();
    this.pedidoService.limparPedidosDoGrupo(arrayHashs).then((resposta) => {
      for (const pedido of resposta.StatusPedidos) {
        if (Number(pedido.ExibirMonitor) === 1) continue;
        this.removerPedidoDoKanban(pedido.id);
      }
    });
  }

  setarPedidoParaNaoExibirMaisNoMonitor(kanbanPedido: KanbanPedido) {
    kanbanPedido.registrarStatusInicioAtualizacaoNoServidor();

    const arrayHashs = [kanbanPedido.pedido.Hash];
    this.pedidoService.limparPedidosDoGrupo(arrayHashs).then((resposta) => {
      for (const pedido of resposta.StatusPedidos) {
        if (Number(pedido.ExibirMonitor) === 1) continue;
        this.removerPedidoDoKanban(pedido.id);
      }
    });
  }

  limparMonitor() {
    if (!this.idPedidoMaisRecente) return;

    this.iterarTodosOsPedidosDoKanban((grupo, pedido) => {
      if (pedido.idDoPedido > this.idPedidoMaisRecente) return;
      pedido.registrarStatusInicioAtualizacaoNoServidor();
    });

    this.pedidoService
      .limparMonitor(this.idPedidoMaisRecente)
      .then((resposta) => {
        this.iterarTodosOsPedidosDoKanban((grupo, kanbanPedido) => {
          if (kanbanPedido.idDoPedido > this.idPedidoMaisRecente) return;
          this.removerPedidoDoKanban(kanbanPedido.pedido.id);
        });
      });
  }

  exibirSomentePedidosDoDia() {
    this.pedidoService.getPedidosDoDia().then((resposta) => {
      const listaIdPedidosNoKanban = this.listaDeIdDosPedidosNoKanban;
      const listaIdPedidosDoDia = resposta.Pedidos.map((item) => item.id);

      for (const pedido of resposta.Pedidos) {
        if (!listaIdPedidosNoKanban.includes(String(pedido.id))) {
          this.adicionarItem(new KanbanPedido(pedido));
        }
      }

      for (const idPedido of listaIdPedidosNoKanban) {
        if (!listaIdPedidosDoDia.includes(idPedido)) {
          this.removerPedidoDoKanban(idPedido);
        }
      }
    });
  }
}
